import type { FieldValues } from 'react-hook-form';
import { useSession } from '@hooks/security/use-session';
import { useScreeningSummary } from '@/hooks/register/screening/use-screening-summary';
import { useLocalize, type TLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { ReactNode } from 'react';
import type { IScreeningSummaryModel, IScreeningSummarySubmitResult } from '@/types/screening/screening-summary';
import PageLoader from '@components/page-loader';
import ScreeningProgress from '@views/register/screening/screening-progress';

const ScreeningSummary = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useScreeningSummary(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <ScreeningSummaryContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IScreeningSummaryContentProps {
    model: IScreeningSummaryModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: () => Promise<IScreeningSummarySubmitResult>;
}

/** Renders a yes / no / not-specified answer from a nullable boolean. */
const yesNo = (t: TLocalize, value: boolean | null): string =>
    value == null
        ? t('DashLabel_NotSpecified', '[Not specified]')
        : value
            ? t('Word_Yes', 'Yes')
            : t('Word_No', 'No');

interface IRowProps {
    label: ReactNode;
    value: ReactNode;
    /** Renders the label as an indented sub-question rather than a top-level label. */
    indent?: boolean;
}

const Row = ({ label, value, indent }: IRowProps) => (
    <>
        {!indent ?
            <div className="row row-margined">
                <div className="col-xs-12 col-md-6 col-lg-6">
                    <label>{label}</label>
                </div>
                <div className="col-xs-12 col-md-4 col-lg-4">{value}</div>
            </div>
            :
            <>
                <div className="row row-margined hidden-xs">
                    <div className="col-xs-12 col-md-6 col-lg-6">
                        <span style={{ paddingLeft: 10 }}>{label}</span>
                    </div>
                    <div className="col-xs-12 col-md-4 col-lg-4">{value}</div>
                </div>

                <div className="row row-margined show-xs">
                    <div className="col-xs-12">
                        <div style={{ marginLeft: 10 }}>{label}: {value}</div>
                    </div>
                </div>
            </>
        }
    </>
);

const ScreeningSummaryContent = ({ model, isSubmitting, submitError, onSubmit }: IScreeningSummaryContentProps) => {
    const t = useLocalize(model.languageData);

    const notSpecified = t('DashLabel_NotSpecified', '[Not specified]');
    const isLiver = model.isLiver;
    const isFemale = model.isFemale;

    const { onSubmit: onDone } = useStepForm<FieldValues, IScreeningSummarySubmitResult>({
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    return (
        <>
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                </div>
            )}

            <div className="col-xs-12 col-sm-3 hidden-xs">
                <ScreeningProgress model={model} overrideStep={DashRegistrationStep.ScreeningSummary} />
            </div>

            <div className="col-xs-12 col-sm-9 summary-mobile-content">
                <div className='middle-content'>
                    <style>{`.screening-Summary-rows > .row:nth-child(even) { background-color: #f7f7f7; }`}</style>

                    <h1>{t('DashLabel_ScreeningSummary', 'Screening Summary')}</h1>

                    <hr />

                    <div style={{ marginBottom: 20 }}>
                        {t(
                            'DashLabel_PleaseReviewYourAnswersBelowTheseAnswersWillBeUsed',
                            "Please review your answers below. These responses are used to determine whether it is safe and possible for you to donate. To correct an answer, click the relevant item under Screening Questions on the right to return to that section and edit it. When all answers are correct, click Done to submit. After submission, you won't be able to make additional changes",
                        )}
                        .
                    </div>

                    {/* Personal */}
                    {model.isQuickStartDonor ? (
                        <div className="container-fluid screening-summary-rows" style={{ paddingLeft: 0 }}>
                            steve
                            <Row
                                label={t('DashLabel_LegalName', 'Legal Name')}
                                value={`${model.firstName} ${model.lastName}`}
                            />
                            <Row label={t('DashLabel_PhoneNumber', 'Phone Number')} value={model.phoneNumber} />
                            {/* <Row
                                label={t('DashLabel_AlternatePhoneNumber', 'Alternate Phone Number')}
                                value={model.phoneNumberSecondary || notSpecified}
                            /> */}
                            <Row label={t('DashLabel_PostalCode', 'Zip Code')} value={model.postalCode} />
                            <Row label={t('Word_Gender', 'Gender')} value={model.genderText} />
                            <Row label={t('DashLabel_BirthDate', 'Birth Date')} value={model.birthDateText} />
                            <Row
                                label={t('Word_Height', 'Height')}
                                value={`${model.heightFeet} ft. / ${model.heightInches} in.`}
                            />
                            <Row label={t('Word_Weight', 'Weight')} value={`${model.weightLbs ?? 0} lbs.`} />
                            <Row
                                label={t(
                                    'DashLabel_AreYouCurrentlyResidingInTheUnitedStates',
                                    'Are you currently residing in the United States',
                                )}
                                value={yesNo(t, model.isResidingInUsa)}
                            />
                            {model.isResidingInUsa !== true && (
                                <Row
                                    label={t(
                                        'DashLabel_AreYouAbleToGetToTheUSByYourOwnMeansWithoutFinancial',
                                        'Are you able to get to the U.S. by your own means without financial assistance',
                                    )}
                                    value={yesNo(t, model.isAbleToGetToUsa)}
                                />
                            )}
                            {/* <Row
                                label={t('DashLabel_AreYouCurrentlyIncarcerated', 'Are you currently incarcerated')}
                                value={yesNo(t, model.isCurrentlyIncarcerated)}
                            /> */}
                        </div>
                    ) : (
                        <>
                            <div className="container-fluid screening-Summary-rows" style={{ paddingLeft: 0 }}>
                                <Row
                                    label={t('DashLabel_LegalName', 'Legal Name')}
                                    value={`${model.firstName} ${model.lastName}`}
                                />
                                <Row
                                    label={t('Word_Address', 'Address')}
                                    value={
                                        <>
                                            <div>{model.countryOfResidenceText}</div>
                                            <div>{model.addressLine1}</div>
                                            <div>
                                                {model.city}, {model.stateProvince} {model.postalCode}
                                            </div>
                                            <div>{model.phoneNumber}</div>
                                            <div>{model.phoneNumberSecondary}</div>
                                        </>
                                    }
                                />
                                <Row
                                    label={t('DashLabel_PhoneNumberS', 'Phone Number(s)')}
                                    value={
                                        <>
                                            <div>
                                                {t('Word_Primary', 'Primary')}: {model.phoneNumber}
                                            </div>
                                            <div>
                                                {t('Word_Alternate', 'Alternate')}:{' '}
                                                {model.phoneNumberSecondary || notSpecified}
                                            </div>
                                        </>
                                    }
                                />
                                <Row label={t('Word_Gender', 'Gender')} value={model.genderText} />
                                <Row
                                    label={t('DashLabel_RaceEthnicity', 'Race/Ethnicity')}
                                    value={model.ethnicityText}
                                />
                                {model.hasSsn ? (
                                    <Row label="SSN" value={model.ssnMasked} />
                                ) : (
                                    <Row
                                        label={t('DashLabel_GovernmentIssuedId', 'Government Issued ID')}
                                        value={
                                            <>
                                                <div>
                                                    {t('DashLabel_IssuingCountry', 'Issuing Country')}:{' '}
                                                    {model.dlIssueCountryText}
                                                </div>
                                                <div>
                                                    {t('DashLabel_IssuingState', 'Issuing State')}:{' '}
                                                    {model.dlIssueStateText}
                                                </div>
                                                <div>DL/State ID #: {model.dlNumber}</div>
                                                <div>
                                                    {t("DashLabel_MotherSMaidenName", "Mother's Maiden Name")}:{' '}
                                                    {model.mothersMaidenName}
                                                </div>
                                            </>
                                        }
                                    />
                                )}
                                <Row
                                    label={t(
                                        'DashLabel_AreYouCurrentlyResidingInTheUnitedStates',
                                        'Are you currently residing in the United States',
                                    )}
                                    value={yesNo(t, model.isResidingInUsa) + '?'}
                                />
                                <Row
                                    label={t(
                                        'DashLabel_AreYouCurrentlyIncarcerated',
                                        'Are you currently incarcerated',
                                    )}
                                    value={yesNo(t, model.isCurrentlyIncarcerated)}
                                />
                            </div>

                            <div className="container-fluid screening-Summary-rows" style={{ paddingLeft: 0 }}>
                                <Row
                                    label={t('DashLabel_PreferredLanguage', 'PreferredLanguage')}
                                    value={model.preferredLanguageText}
                                />
                                <Row
                                    label={t('DashLabel_BestWorkWeekContactMethod', 'Best work week contact method')}
                                    value={model.contactPreferenceText}
                                />
                                <Row
                                    label={t(
                                        'DashLabel_BestBusinessHoursContactTimeFrame',
                                        'Best business hours contact time frame',
                                    )}
                                    value={model.contactBestHoursText}
                                />
                                <Row
                                    label={t('DashLabel_EmergencyContact', 'Emergency Contact')}
                                    value={
                                        <>
                                            <div>
                                                {t('Word_Name', 'Name')}: {model.emergencyContact}
                                            </div>
                                            <div>
                                                {t('Word_Phone', 'Phone')}: {model.emergencyContactPhone}
                                            </div>
                                            <div>
                                                {t('Word_Relationship', 'Relationship')}:{' '}
                                                {model.emergencyContactRelationshipText}
                                            </div>
                                        </>
                                    }
                                />
                                <Row
                                    label={t('DashLabel_MaritalStatus', 'Marital Status')}
                                    value={model.maritalStatusText}
                                />
                                <Row
                                    label={t('DashLabel_WorkStatus', 'Work Status')}
                                    value={model.employmentStatusText}
                                />
                                <Row label={t('Word_Occupation', 'Occupation')} value={model.occupation} />
                                <Row
                                    label={t(
                                        'DashLabel_OccupationRequiresHeavyLifting',
                                        'Occupation Requires Heavy Lifting',
                                    )}
                                    value={yesNo(t, model.occupationHasHeavyLifting)}
                                />
                                <Row
                                    label={t('DashLabel_ExerciseRoutine', 'Exercise Routine')}
                                    value={model.exerciseRoutineText}
                                />
                                <Row
                                    label={t('DashLabel_HealthInsurance', 'Health Insurance')}
                                    value={yesNo(t, model.hasHealthInsurance)}
                                />
                                <Row
                                    label={t('Word_Physician', 'Physician')}
                                    value={
                                        <>
                                            <div>
                                                {t('Word_Name', 'Name')}: {model.physicianName}
                                            </div>
                                            <div>City: {model.physicianCity}</div>
                                            <div>State: {model.physicianStateText}</div>
                                        </>
                                    }
                                />
                                <Row
                                    label={t('Word_Height', 'Height')}
                                    value={`${model.heightFeet} ft. / ${model.heightInches} in.`}
                                />
                                <Row label={t('Word_Weight', 'Weight')} value={`${model.weightLbs ?? 0} lbs.`} />
                                <Row
                                    label={t('Word_Motivation', 'Motivation')}
                                    value={model.motivationToDonate}
                                />
                            </div>
                        </>
                    )}

                    {/* Medical */}
                    <div className="container-fluid screening-summary-rows" style={{ paddingLeft: 0 }}>
                        <Row
                            label={t('DashLabel_AcceptBloodTransfusions', 'Accept Blood Transfusions')}
                            value={yesNo(t, model.willAcceptTransfusions)}
                        />

                        {!isLiver && (
                            <Row
                                label={t('DashLabel_HasNutcracker', 'Do you have Nutcracker Syndrome')}
                                value={yesNo(t, model.hasNutcracker)}
                            />
                        )}

                        <Row
                            label={t(
                                'DashLabel_DiagnosedWithPolycysticKidneyDiseasePkdIgaNephropathy',
                                'Diagnosed with Polycystic Kidney Disease (PKD), IgA nephropathy, Lupus Nephritis, or Glomerulonephritis',
                            )}
                            value={yesNo(t, model.hasHadKidneyDisease)}
                        />

                        <Row
                            label={t('DashLabel_HaveYouEverHadAHeartAttack', 'Have you ever had a heart attack')}
                            value={yesNo(t, model.hasHadHeartAttack)}
                        />
                        {model.hasHadHeartAttack === true && (
                            <Row
                                indent
                                label={t('DashLabel_HowLongSinceYourHeartAttack', 'How long since your heart attack')}
                                value={model.heartAttackRemissionText}
                            />
                        )}

                        <Row
                            label={t('DashLabel_DoYouHaveDiabetes', 'Do you have diabetes')}
                            value={yesNo(t, model.hasDiabetes)}
                        />
                        {model.hasDiabetes === true && (
                            <Row
                                indent
                                label={t(
                                    'DashLabel_WhatBestDescribesYourMethodOfBloodSugarControl',
                                    'How do you control your blood sugar',
                                )}
                                value={model.diabetesStatusText}
                            />
                        )}

                        <Row
                            label={t(
                                'DashLabel_HaveYouEverBeenDiagnosedWithCancerIncludingSkinCan',
                                'Have you ever been diagnosed with cancer (including skin cancer)',
                            )}
                            value={yesNo(t, model.hasHadCancer)}
                        />
                        {model.hasHadCancer === true && (
                            <div className="row row-margined">
                                <div className="col-xs-12 col-md-12 col-lg-12">
                                    <span style={{ paddingLeft: 10 }}>
                                        {t('DashLabel_ListOfCancers', 'List of cancers')}:
                                    </span>{' '}
                                    {model.cancers || notSpecified}
                                </div>
                            </div>
                        )}

                        {!isLiver && (
                            <>
                                <Row
                                    label={t(
                                        'DashLabel_HaveYouEverBeenDiagnosedWithKidneyStones',
                                        'Have you ever been diagnosed with kidney stones',
                                    )}
                                    value={yesNo(t, model.hasHadKidneyStones)}
                                />
                                {model.hasHadKidneyStones === true && (
                                    <>
                                        <Row
                                            indent
                                            label={t(
                                                'DashLabel_HowManyTimesHaveYouHadKidneyStones',
                                                'How many times have you had kidney stones',
                                            )}
                                            value={model.kidneyStoneEpisodesText}
                                        />
                                        <Row
                                            indent
                                            label={t(
                                                'DashLabel_HowLongSinceYourLastEpisode',
                                                'How long since your last episode',
                                            )}
                                            value={model.kidneyStoneRemissionText}
                                        />
                                        <Row
                                            indent
                                            label={t(
                                                'DashLabel_HowWereTheMostRecentStonesTreated',
                                                'How were the most recent stones treated',
                                            )}
                                            value={model.kidneyStoneTreatmentText}
                                        />
                                    </>
                                )}
                            </>
                        )}

                        {isFemale && (
                            <>
                                <Row
                                    label={t('DashLabel_HaveYouBeenPregnant', 'Have you been pregnant')}
                                    value={yesNo(t, model.hasBeenPregnant)}
                                />
                                {model.hasBeenPregnant === true && (
                                    <>
                                        <Row
                                            indent
                                            label={t(
                                                'DashLabel_HaveYouBeenTreatedForGestationalDiabetes',
                                                'Have you been treated for gestational diabetes',
                                            )}
                                            value={yesNo(t, model.hadGestationalDiabetes)}
                                        />
                                        <Row
                                            indent
                                            label={t('DashLabel_AreYouCurrentlyPregnant', 'Are you currently pregnant')}
                                            value={yesNo(t, model.isPregnant)}
                                        />
                                        {model.isPregnant === false && (
                                            <Row
                                                indent
                                                label={t(
                                                    'DashLabel_MonthsSinceLastPregnancy',
                                                    'Months since last pregnancy',
                                                )}
                                                value={model.monthsSincePregnant}
                                            />
                                        )}
                                        <Row
                                            indent
                                            label={t('DashLabel_AreYouCurrentlyNursing', 'Are you currently nursing')}
                                            value={yesNo(t, model.isNursing)}
                                        />
                                    </>
                                )}
                            </>
                        )}

                        <Row
                            label={t(
                                'DashLabel_HaveYouEverUsedTobaccoProducts',
                                'Have you ever used tobacco or nicotine products, including cigarettes, chewing tobacco, vaping, gums, pouches, patches, lozenges, hookah, or anything else with nicotine',
                            )}
                            value={yesNo(t, model.hasUsedTobacco)}
                        />
                        {model.hasUsedTobacco === true && (
                            <>
                                <Row
                                    indent
                                    label={t(
                                        'DashLabel_DoYouCurrentlyUseTobaccoProducts',
                                        'Do you currently use any nicotine products',
                                    )}
                                    value={yesNo(t, model.usesTobacco)}
                                />
                                <Row
                                    indent
                                    label={t(
                                        'DashLabel_AreYouWillingAndAbleToStopUsingTobaccoProductsForAMinimum',
                                        'Are you willing and able to stop using all nicotine products before your donation surgery',
                                    )}
                                    value={yesNo(t, model.willStopTobaccoUse)}
                                />
                            </>
                        )}

                        <Row
                            label={t('DashLabel_DoYouHaveAHistoryOfHiv', 'Have you ever been diagnosed with HIV')}
                            value={model.hasHivText}
                        />
                    </div>

                    {/* Donation */}
                    <div className="container-fluid screening-Summary-rows" style={{ paddingLeft: 0 }}>
                        {!isLiver && (
                            <Row
                                label={t(
                                    'DashLabel_WhatBestDescribesYourCurrentSituation',
                                    'Who do you want to help with your donation',
                                )}
                                value={model.donorSituationText}
                            />
                        )}

                        {model.isDirectedDonor && (
                            <>
                                <Row
                                    label={t('DashLabel_WhoDoYouWantToHelp', 'Who do you want to help')}
                                    value={model.ddRelationshipText}
                                />

                                <div className="row row-margined">
                                    <div className="col-xs-12 col-md-8 col-lg-8">
                                        <label>{t('Word_Recipient', 'Recipient')}</label>
                                    </div>
                                </div>

                                <Row
                                    indent
                                    label={t("DashLabel_RecipientSName", "Recipient's name")}
                                    value={model.recipientName}
                                />
                                <Row
                                    indent
                                    label={t(
                                        'DashLabel_DoYouKnowTheRecipientSDateOfBirth',
                                        "Do you know the recipient's date of birth",
                                    )}
                                    value={yesNo(t, model.knowsRecipientDob)}
                                />
                                <Row
                                    indent
                                    label={t("DashLabel_RecipientSDateOfBirth", "Recipient's date of birth")}
                                    value={model.recipientDobText}
                                />

                                {model.isDcnSite || model.isNkrSite ? (
                                    <Row
                                        label={t(
                                            'DashLabel_WhatTransplantCenterIsThePatientAt',
                                            'What transplant center is the patient at',
                                        )}
                                        value={model.recipientCenterDisplay}
                                    />
                                ) : (
                                    <Row
                                        label={`${t('DashLabel_IsYourRecipientCurrentlyAt', 'Is your recipient currently at')} ${model.siteName}`}
                                        value={yesNo(t, model.recipientAtSameDashCenter)}
                                    />
                                )}

                                {model.recipientCenterId === -1 && (
                                    <Row
                                        label={`${t('DashLabel_IsThePatientWillingToTransferTo', 'Is the patient willing to transfer to')} ${model.recipientCenter} ${t('DashLabel_CenterIfNecessary', 'center if necessary')}`}
                                        value={yesNo(t, model.recipientIsWillingToTransfer)}
                                    />
                                )}

                                {model.isRemoteDonationPreferred !== null &&
                                    (model.isNkrSite || model.isDcnSite) ? (
                                    <>
                                        <div className="row row-margined">
                                            <div className="col-xs-12 col-md-8 col-lg-8">
                                                <label>
                                                    {model.isRdnCenter
                                                        ? t(
                                                            'DashLabel_YourRecipientSCenterIsARemoteDonationNetworkRdnCen',
                                                            "Your recipient's center is a Remote Donation Network (RDN) center, and it appears that there is an RDN center closer to your home",
                                                        )
                                                        : t(
                                                            'DashLabel_YourRecipientSCenterIsNotARemoteDonationNetworkRdn',
                                                            "Your recipient's center is not a Remote Donation Network (RDN) center, but there is an RDN center closer to your home",
                                                        )}
                                                    .
                                                </label>
                                            </div>
                                            <div className="col-xs-12 col-md-4 col-lg-4"></div>
                                        </div>
                                        <Row
                                            indent
                                            label={t(
                                                'DashLabel_WouldYouPreferToDonateAtAnRdnCenterNearYou',
                                                'Would you prefer to donate at an RDN center near you',
                                            )}
                                            value={yesNo(t, model.isRemoteDonationPreferred)}
                                        />
                                        <Row
                                            label={`${t('DashLabel_ToBetterUnderstandTheSituationPleaseLetUsKnowWhyYou', "To better understand the situation, please let us know why you don't want to donate remotely")}...`}
                                            value={model.whyNotRemote || notSpecified}
                                        />
                                    </>
                                ) : (
                                    model.isRemoteDonationPreferred !== null && (
                                        <>
                                            <Row
                                                label={t(
                                                    'DashLabel_WillItBeDifficultForYouToTravelToTheRecipientSTransplant',
                                                    "Will it be difficult for you to travel to the recipient's transplant center twice, first for the workup and again for the surgery",
                                                )}
                                                value={yesNo(t, model.isHardshipToTravel)}
                                            />
                                            <Row
                                                label={t(
                                                    'DashLabel_WouldYouPreferToDonateAtACenterNearYou',
                                                    'Would you prefer to donate at a center near you',
                                                )}
                                                value={yesNo(t, model.isRemoteDonationPreferred)}
                                            />
                                        </>
                                    )
                                )}
                            </>
                        )}
                    </div>

                    {submitError && (
                        <div className="row row-margined">
                            <div className="col-xs-12">
                                <div className="alert alert-danger">{submitError}</div>
                            </div>
                        </div>
                    )}

                    <div className="form-group" id="ContinueBlock">
                        <button
                            type="button"
                            className="btn btn-primary btn-lg block"
                            style={{ marginTop: 20 }}
                            disabled={isSubmitting}
                            onClick={onDone}
                        >
                            {t('Word_Done', 'Done')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ScreeningSummary;
